import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import AnimatedText from "../../components/ui/AnimatedText";

import heroBg1 from "../../assets/images/hero-bg1.webp";
import heroBg2 from "../../assets/images/hero-bg2.webp";
import heroBg3 from "../../assets/images/hero-bg3.webp";
import heroBg4 from "../../assets/images/hero-bg4.webp";

const HeroSection = () => {
  const selectedBg = useMemo(() => {
    const backgrounds = [heroBg1, heroBg2, heroBg3, heroBg4];
    const index = Math.floor(Math.random() * backgrounds.length);
    return backgrounds[index];
  }, []);

  const [formData, setFormData] = useState({
    tipo: "",
    nome: "",
    telefone: "",
  });
  const [success, setSuccess] = useState(false);
  const [telError, setTelError] = useState(false);

  // Função para aplicar máscara brasileira no telefone
  const formatPhone = (value) => {
    // Remove tudo que não é número
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 11)
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(
      7,
      11
    )}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "telefone") {
      const masked = formatPhone(value);
      setFormData((prev) => ({ ...prev, telefone: masked }));
      setTelError(false);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validação mínima telefone - pelo menos 10 dígitos (sem máscara)
    const digits = formData.telefone.replace(/\D/g, "");
    if (digits.length < 10) {
      setTelError(true);
      return;
    }

    const formUrl =
      "https://docs.google.com/forms/d/e/1FAIpQLScQc-SPygWVNQn0Zl1IUIqvtMl73Xa1sa_6qW7IaBalG3J5Ug/formResponse";

    const formBody = new URLSearchParams({
      "entry.426038373": formData.tipo,
      "entry.371468472": formData.nome,
      "entry.684960318": formData.telefone,
    });

    await fetch(formUrl, {
      method: "POST",
      mode: "no-cors",
      body: formBody,
    });

    setSuccess(true);
    setFormData({ tipo: "", nome: "", telefone: "" });
    setTelError(false);
  };

  return (
    <section
      className="hero-section bg-cover bg-center min-h-screen pt-16 md:pt-28 lg:32"
      style={{
        backgroundImage: `radial-gradient(circle, rgba(60, 200, 252, 0.45) 0%, rgba(9, 25, 55, 0.9) 100%), url(${selectedBg})`,
      }}
    >
      <div className="container mx-auto px-4 py-20">
        {/* DESKTOP */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 1, ease: "easeOut" }}
          className="hidden md:flex items-center"
        >
          <div className="w-1/2 text-white pr-10">
            <h1 className="xl:text-5xl md:text-3xl font-bold mb-6">
              <AnimatedText
                text="Proteja o que importa com a Free Market Seguros"
                delay={2}
              />
            </h1>
            <p className="xl:text-xl md:text-sm mb-8">
              <AnimatedText
                text={`Trabalhamos com as melhores seguradoras \n para oferecer a proteção ideal para \nvocê, sua família e seu patrimônio.`}
                delay={2.3}
              />
            </p>
            <div className="flex space-x-4">
              <Link
                to="/auto"
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-md font-medium text-center"
              >
                <span className="hidden md:block">Simular Auto</span>
              </Link>
              <a
                href="#servicos"
                className="bg-white text-blue-900 hover:bg-gray-100 px-6 py-3 rounded-md font-medium text-center"
              >
                <span className="hidden md:block">Ver Todos</span>
              </a>
            </div>
          </div>

          <div className="w-1/2 flex justify-center">
            <div className="bg-white bg-opacity-40 backdrop-blur-sm p-8 rounded-3xl shadow-xl max-w-xs md:w-full">
              <h3 className="text-center md:text-lg lg:text-2xl text-blue-900 mb-6">
                Contrate agora!
              </h3>

              {success ? (
                <p className="text-center text-green-800 font-medium">
                  Obrigado! Em breve entraremos em contato.
                </p>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <select
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleChange}
                    required
                    className="w-full appearance-none p-3 pl-7 bg-white/50 rounded-3xl text-gray-700 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-400 transition md:text-xs lg:text-sm"
                  >
                    <option value="">Selecione o tipo de seguro</option>
                    <option value="Automovel">Seguro Auto</option>
                    <option value="Saude">Plano de Saúde</option>
                    <option value="Agricola">Seguro Agrícola</option>
                    <option value="Previdencia">Previdência</option>
                    <option value="Vida">Seguro de Vida</option>
                    <option value="Consorcio">Consórcio</option>
                  </select>

                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    placeholder="Nome"
                    className="w-full p-3 pl-7 bg-white/50 rounded-3xl text-gray-700 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-400 transition md:text-xs lg:text-sm"
                  />

                  <input
                    type="tel"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleChange}
                    required
                    placeholder="Telefone/WhatsApp"
                    className={`w-full p-3 pl-7 bg-white/50 rounded-3xl text-gray-700 placeholder-gray-600 focus:outline-none focus:ring-2 transition md:text-xs lg:text-sm ${
                      telError
                        ? "focus:ring-red-500 border border-red-500"
                        : "focus:ring-orange-400 border border-transparent"
                    }`}
                  />
                  {telError && (
                    <p className="text-red-600 text-xs -mt-3 mb-3">
                      Por favor, insira um telefone válido com DDD.
                    </p>
                  )}

                  <button
                    type="submit"
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white p-3 rounded-3xl font-semibold transition shadow-sm hover:shadow-md md:text-xs lg:text-sm"
                  >
                    Solicitar
                  </button>
                </form>
              )}
            </div>
          </div>
        </motion.div>

        {/* MOBILE */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 1, ease: "easeOut" }}
          className="md:hidden flex flex-col items-center"
        >
          <div className="text-white mb-10 px-4 text-left">
            <h1 className="text-3xl font-bold mb-7">
              <AnimatedText
                text="Proteja o que importa com a Free Market Seguros"
                delay={2}
              />
            </h1>
            <p className="text-lg mb-6">
              <AnimatedText
                text={`Trabalhamos com as melhores seguradoras \n para oferecer a proteção ideal para \nvocê, sua família e seu patrimônio.`}
                delay={2.3}
              />
            </p>

            <div className="flex flex-col space-y-3 items-start">
              <Link
                to="/simulacao"
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-md font-medium text-center w-auto"
              >
                Simular Auto
              </Link>
              <a
                href="#servicos"
                className="bg-white text-blue-900 hover:bg-gray-100 px-6 py-3 rounded-md font-medium text-center w-auto"
              >
                Ver todos
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
